import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { adminApi, setNoindex } from '../lib/adminApi';
import { Lock, Mail, ShieldCheck, ArrowRight } from 'lucide-react';

const AdminRegisterPage = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const undo = setNoindex();
    document.title = 'Admin Setup — Dionlovelle';
    return undo;
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) {
      setError('Missing invitation token. Ask the site operator for a fresh link.');
      return;
    }
    if (password.length < 12) {
      setError('Password must be at least 12 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await adminApi.registerViaInvite(token, email.trim(), password);
      navigate('/admin', { replace: true });
    } catch (err) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[color:var(--ink)] text-[color:var(--ivory)] flex items-center justify-center px-5 py-10"
         style={{ background: 'radial-gradient(120% 80% at 50% 0%, #16223A 0%, #0B0F1A 55%, #06070C 100%)' }}
         data-testid="admin-register-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <p className="text-[10px] tracking-[0.42em] text-[color:var(--gold)] mb-3">✦ INVITATION ✦</p>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold mb-2">Set your password</h1>
          <p className="text-sm text-[color:var(--ivory-muted)]/70">
            One-time link. Valid for a short window.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="glass-card p-6 sm:p-8 space-y-4" data-testid="admin-register-form">
          <label className="block">
            <span className="section-eyebrow block mb-2">Email</span>
            <div className="relative">
              <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 opacity-50" />
              <input
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@dionlovelle.com"
                className="dl-input pl-10"
                data-testid="admin-register-email"
              />
            </div>
          </label>
          <label className="block">
            <span className="section-eyebrow block mb-2">New password (min 12)</span>
            <div className="relative">
              <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 opacity-50" />
              <input
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 12 characters"
                className="dl-input pl-10"
                data-testid="admin-register-password"
              />
            </div>
          </label>
          <label className="block">
            <span className="section-eyebrow block mb-2">Confirm password</span>
            <div className="relative">
              <ShieldCheck size={16} className="absolute left-4 top-1/2 -translate-y-1/2 opacity-50" />
              <input
                type="password"
                autoComplete="new-password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Confirm your password"
                className="dl-input pl-10"
                data-testid="admin-register-confirm"
              />
            </div>
          </label>

          {error && <p className="text-sm text-red-300 text-center" data-testid="admin-register-error">{error}</p>}

          <button type="submit" disabled={busy} className="glow-button w-full disabled:opacity-70" data-testid="admin-register-submit">
            {busy ? 'CREATING…' : (<>CREATE ADMIN ACCOUNT <ArrowRight size={16} strokeWidth={2.5} /></>)}
          </button>

          <p className="text-[11px] text-[color:var(--ivory-muted)]/50 text-center">
            Your password is hashed on the server using bcrypt and stored only as a hash. This page is not indexed.
          </p>
        </form>
      </div>
    </div>
  );
};

export default AdminRegisterPage;
